import React, { createContext, useContext } from "react";
import { Audio, Sequence, staticFile } from "remotion";

/**
 * B2B partner video — sound effects.
 *
 * Files come from `scripts/b2b-audio.sh` (sources + licenses there): each
 * starts on its onset and peaks at -3 dBFS. `lead` = frames from the start
 * of the file to its peak, so `<Sfx at={n}>` makes the *hit* land on frame
 * `n` (a whoosh starts before the cut it lands on). `volume` sets the
 * palette's balance; cues scale it with their own `volume`.
 *
 * Palette rule: one sound per visual event, soft and short, never two
 * accents on the same frame. The music bed (see `B2BVideo`) sits ~6 dB
 * under the effects.
 */

type Sound = { lead: number; frames: number; volume: number };

export const SOUNDS = {
  swoosh: { lead: 2, frames: 14, volume: 0.38 }, //    scene cuts
  slide: { lead: 3, frames: 18, volume: 0.65 }, //    in-scene switch
  impact: { lead: 24, frames: 78, volume: 0.6 }, //   the reveal
  pop: { lead: 0, frames: 6, volume: 0.6 }, //       a card / button pops in
  "bubble-in": { lead: 0, frames: 9, volume: 0.55 }, // chat bubble received
  "bubble-out": { lead: 1, frames: 11, volume: 0.45 }, // chat bubble sent
  negative: { lead: 2, frames: 12, volume: 0.4 }, //  red: CAC up, "Direct"
  click: { lead: 0, frames: 5, volume: 0.55 }, //     tap
  key: { lead: 0, frames: 5, volume: 0.4 }, //        keystroke
  confirm: { lead: 0, frames: 18, volume: 0.45 }, //  link copied
  notify: { lead: 3, frames: 9, volume: 0.5 }, //     new sale
  coins: { lead: 1, frames: 24, volume: 0.55 }, //    ambassador paid
  strike: { lead: 1, frames: 11, volume: 0.7 }, //   strike-through
  sparkle: { lead: 10, frames: 48, volume: 0.6 }, //  CTA sheen
  tick: { lead: 0, frames: 2, volume: 0.5 }, //      counters, logos
} satisfies Record<string, Sound>;

export type SoundKey = keyof typeof SOUNDS;

/** Off for the silent comparison render (`sfx: false` prop). */
export const SfxContext = createContext(true);

/** One sound effect, its peak on local frame `at`. */
export const Sfx: React.FC<{
  sound: SoundKey;
  at: number;
  volume?: number;
}> = ({ sound, at, volume = 1 }) => {
  const on = useContext(SfxContext);
  if (!on) return null;
  const s = SOUNDS[sound];
  return (
    <Sequence
      from={Math.round(at) - s.lead}
      durationInFrames={s.frames}
      layout="none"
      name={`sfx ${sound}`}
    >
      <Audio
        src={staticFile(`b2b/sfx/${sound}.mp3`)}
        volume={() => s.volume * volume}
      />
    </Sequence>
  );
};

/** Ticks every `every` frames over [from, to) — a counter running. */
export const Ticks: React.FC<{
  from: number;
  to: number;
  every?: number;
  volume?: number;
}> = ({ from, to, every = 3, volume = 1 }) => {
  const frames: number[] = [];
  for (let f = from; f < to; f += every) frames.push(f);
  return (
    <>
      {frames.map((f) => (
        <Sfx key={f} sound="tick" at={f} volume={volume} />
      ))}
    </>
  );
};
